export default class TextCarouselModule {
	private carouselElement: HTMLElement | null = null;
	private slideContainer: HTMLElement | null = null;
	private originalSpans: HTMLElement[] = [];
	private isInitialized: boolean = false;

	constructor() {
		this.carouselElement = document.querySelector('[data-control-text]');
		this.slideContainer = document.querySelector('[data-control-slide]');

		if (this.slideContainer) {
			this.originalSpans = Array.from(this.slideContainer.querySelectorAll('span'));
		}
	}

	/**
	 * Initialize the text carousel animation
	 * Creates a true carousel effect with continuous rotation
	 */
	public init(): void {
		if (this.isInitialized) {
			console.warn('TextCarouselModule: Already initialized');
			return;
		}

		if (!this.carouselElement || !this.slideContainer || this.originalSpans.length < 2) {
			console.error('TextCarouselModule: Required elements not found or insufficient spans');
			return;
		}

		this.setupCarousel();
		this.isInitialized = true;

		console.log('TextCarouselModule: Initialized successfully');
	}

	/**
	 * Setup the carousel by duplicating items for seamless loop
	 */
	private setupCarousel(): void {
		if (!this.slideContainer) return;

		// Store original content
		const originalContent = this.slideContainer.innerHTML;

		// Create seamless loop: original content + duplicate
		this.slideContainer.innerHTML = originalContent + originalContent;

		// Apply animations
		this.applyCarouselAnimations();

		const allSpans = this.slideContainer.querySelectorAll('span');
		if (allSpans.length) {
			// Remove margin-right from the very last span
			const lastSpan = allSpans[allSpans.length - 1] as HTMLElement;
			lastSpan.style.marginRight = '0';
		}
	}

	/**
	 * Apply carousel animations to container and items
	 */
	private applyCarouselAnimations(): void {
		if (!this.slideContainer) return;

		const allSpans = this.slideContainer.querySelectorAll('span');

		// Apply container animation
		this.slideContainer.style.animation = 'carouselMove 8s ease-in-out infinite';

		// Apply individual span animations
		allSpans.forEach((span, index) => {
			const htmlSpan = span as HTMLElement;

			// Apply opacity animations based on original content
			if (index % 2 === 0) {
				// Running items (original positions 0, 2)
				htmlSpan.style.animation = 'runningOpacity 8s ease-in-out infinite';
			} else {
				// Netball items (original positions 1, 3)
				htmlSpan.style.animation = 'netballOpacity 8s ease-in-out infinite';
			}
		});
	}

	/**
	 * Stop the carousel animation
	 */
	public stop(): void {
		if (!this.slideContainer) return;

		const allElements = [this.slideContainer, ...Array.from(this.slideContainer.querySelectorAll('span'))];
		allElements.forEach(element => {
			(element as HTMLElement).style.animationPlayState = 'paused';
		});
	}

	/**
	 * Resume the carousel animation
	 */
	public resume(): void {
		if (!this.slideContainer) return;

		const allElements = [this.slideContainer, ...Array.from(this.slideContainer.querySelectorAll('span'))];
		allElements.forEach(element => {
			(element as HTMLElement).style.animationPlayState = 'running';
		});
	}

	/**
	 * Reset the carousel to initial state
	 */
	public reset(): void {
		if (!this.slideContainer) return;

		// Clear all animations
		this.slideContainer.style.animation = 'none';
		this.slideContainer.style.transform = 'translateX(0)';

		// Restore original content only
		this.slideContainer.innerHTML = '';
		this.originalSpans.forEach(span => {
			const restored = span.cloneNode(true) as HTMLElement;
			restored.style.animation = 'none';
			restored.style.opacity = span === this.originalSpans[0] ? '1' : '0.35';
			this.slideContainer!.appendChild(restored);
		});

		this.isInitialized = false;
	}

	/**
	 * Destroy the carousel and clean up
	 */
	public destroy(): void {
		this.reset();
		this.carouselElement = null;
		this.slideContainer = null;
		this.originalSpans = [];
	}

	/**
	 * Check if carousel is currently running
	 */
	public isRunning(): boolean {
		if (!this.slideContainer || !this.isInitialized) return false;

		const computedStyle = window.getComputedStyle(this.slideContainer);
		return computedStyle.animationPlayState !== 'paused';
	}

	/**
	 * Update animation duration for all elements
	 */
	public setDuration(duration: number): void {
		if (!this.slideContainer || duration <= 0) return;

		const allElements = [this.slideContainer, ...Array.from(this.slideContainer.querySelectorAll('span'))];
		allElements.forEach(element => {
			(element as HTMLElement).style.animationDuration = `${duration}s`;
		});
	}

	/**
	 * Set custom animation timing function
	 */
	public setTimingFunction(timingFunction: string): void {
		if (!this.slideContainer) return;

		const allElements = [this.slideContainer, ...Array.from(this.slideContainer.querySelectorAll('span'))];
		allElements.forEach(element => {
			(element as HTMLElement).style.animationTimingFunction = timingFunction;
		});
	}

	/**
	 * Get carousel configuration info
	 */
	public getInfo(): object {
		return {
			isInitialized: this.isInitialized,
			isRunning: this.isRunning(),
			originalItemCount: this.originalSpans.length,
			currentSpanCount: this.slideContainer?.querySelectorAll('span').length || 0,
			containerWidth: this.slideContainer?.offsetWidth || 0
		};
	}
}
